import { NetworkStatus, useQuery } from '@apollo/client';
import { gql } from '__generated__/gql';
import {
  UnitTransferReasonEnumType,
  UnitTransferRequestStatusEnumType,
} from '__generated__/graphql';
import debounce from 'lodash/debounce';
import { FC } from 'react';
import { useNavigate } from 'react-router-dom';

import SidebarLayout, {
  SidebarLayoutContent,
  SidebarLayoutNavigation,
} from 'layouts/SidebarLayout';

import Badge from 'primitives/Badge';
import LoadingIndicator from 'primitives/LoadingIndicator';
import TextField from 'primitives/TextField';

import DataTable from 'components/DataTable';
import ErrorMessage from 'components/ErrorMessage';
import FilterPanel from 'components/FilterPanel';

import unitTransferReasonToReadable from 'utils/enums/unit-transfer-reason-to-readable';
import unitTransferRequestStatusToReadable from 'utils/enums/unit-transfer-request-status-to-readable';
import unitTransferSlaToBadge from 'utils/enums/unit-transfer-sla-to-badge';

const UNIT_TRANSFER_REQUESTS_QUERY = gql(`
  query getUnitTransferRequestsQueue($cursor: ID, $limit: Int, $filters: UnitTransferRequestsFilterType) {
    unitTransferRequests(cursor: $cursor, limit: $limit, filters: $filters) {
      nodes {
        id
        status
        reason
        slaDaysRemaining
        createdAt
        transferor { name }
        transferee { name }
      }
      pageInfo { hasNextPage cursor }
    }
  }
`);

// Shares its thresholds with the investor tracker via unit-transfer-sla-to-badge, so "2d left"
// cannot come to mean two different things depending on which app you are in.
const slaBadge = (slaDaysRemaining?: number | null) => {
  const badge = unitTransferSlaToBadge(slaDaysRemaining);
  if (!badge) return <span className="text-gray-400">-</span>;
  return <Badge label={badge.label} color={badge.color} />;
};

const UnitTransfersPage: FC = () => {
  const navigate = useNavigate();

  const { loading, error, data, refetch, fetchMore, variables, networkStatus } = useQuery(
    UNIT_TRANSFER_REQUESTS_QUERY,
    {
      variables: { limit: 50 },
      notifyOnNetworkStatusChange: true,
    }
  );

  const renderContent = () => {
    if (loading && !data) return <LoadingIndicator />;
    if (error || !data) return <ErrorMessage error={error} refetch={refetch} />;

    const { nodes: unitTransferRequests, pageInfo } = data.unitTransferRequests;

    const filters = [
      {
        type: 'status',
        label: 'Status',
        options: [
          { label: 'All', value: undefined },
          ...Object.values(UnitTransferRequestStatusEnumType).map(status => ({
            label: unitTransferRequestStatusToReadable(status).label,
            value: status,
          })),
        ],
        readable: unitTransferRequestStatusToReadable,
      },
      {
        type: 'reason',
        label: 'Reason',
        options: [
          { label: 'All', value: undefined },
          ...Object.values(UnitTransferReasonEnumType).map(reason => ({
            label: unitTransferReasonToReadable(reason).label,
            value: reason,
          })),
        ],
        readable: unitTransferReasonToReadable,
      },
    ];

    return (
      <>
        <FilterPanel activeFilters={variables?.filters} filters={filters} refetch={refetch} />
        <div className="flex flex-row gap-4 items-center justify-between my-4">
          <TextField
            placeholder="Search transferor, transferee"
            onChange={debounce(
              e =>
                refetch({ filters: { ...variables?.filters, text: e.target.value || undefined } }),
              500
            )}
          />
        </div>
        <DataTable
          data={unitTransferRequests}
          onClick={unitTransferRequest => navigate(`/unit-transfers/${unitTransferRequest.id}`)}
          columns={[
            { label: 'Transferor', fieldName: 'transferor.name' },
            { label: 'Transferee', fieldName: 'transferee.name' },
            {
              label: 'Reason',
              fieldName: 'reason',
              type: 'CUSTOM',
              customValue: value => {
                const { label, color } = unitTransferReasonToReadable(value);
                return <Badge label={label} color={color} />;
              },
            },
            {
              label: 'Status',
              fieldName: 'status',
              type: 'CUSTOM',
              customValue: value => {
                const { label, color } = unitTransferRequestStatusToReadable(value);
                return <Badge label={label} color={color} showIndicator />;
              },
            },
            {
              label: 'SLA',
              fieldName: 'slaDaysRemaining',
              type: 'CUSTOM',
              customValue: value => slaBadge(value),
            },
            { label: 'Created', fieldName: 'createdAt', type: 'DATE' },
          ]}
          hasNextPage={pageInfo.hasNextPage}
          filterLoading={networkStatus === NetworkStatus.setVariables}
          paginationLoading={loading}
          onLoadMore={() => fetchMore({ variables: { cursor: pageInfo.cursor } })}
        />
      </>
    );
  };

  return (
    <SidebarLayout>
      <SidebarLayoutNavigation
        title="Unit Transfers"
        subTitle="Ops queue for unit transfer requests"
      />
      <SidebarLayoutContent>{renderContent()}</SidebarLayoutContent>
    </SidebarLayout>
  );
};

export default UnitTransfersPage;
